import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Edit, Trash2, CheckCircle2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { LIKELINESS_COLORS, LikelinessIcon, formatCurrency, formatDate } from "./constants";

const SubscriptionTable = ({ 
  subscriptions, 
  sortByRenewal, 
  onSortToggle, 
  onEdit, 
  onDelete,
  selectedIds = [],
  onSelectAll,
  onSelectSubscription,
  isAllSelected,
  isSomeSelected
}) => {
  const getLatestRenewal = (sub) => {
    const renewals = sub.renewals || [];
    if (renewals.length > 0) return renewals[renewals.length - 1];
    if (sub.renewal_date) return { renewal_date: sub.renewal_date, renewal_price: sub.renewal_price, paid: sub.renewal_paid, likeliness: sub.likeliness };
    return null;
  };

  const getCurrentLikeliness = (sub) => {
    const latest = getLatestRenewal(sub);
    if (latest && latest.likeliness) return latest.likeliness;
    return sub.likeliness || "YES";
  };

  return (
    <Card className="bg-white">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all"
                  className={isSomeSelected ? "data-[state=checked]:bg-indigo-600" : ""}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              <TableHead>Business</TableHead>
              <TableHead>Likeliness</TableHead>
              <TableHead>Sign Up</TableHead>
              <TableHead>Initial Price</TableHead>
              <TableHead>Renewals</TableHead>
              <TableHead className="cursor-pointer select-none" onClick={onSortToggle}>
                <div className="flex items-center gap-1">
                  Next Renewal
                  {sortByRenewal === "asc" ? <ArrowUp className="w-4 h-4" /> : sortByRenewal === "desc" ? <ArrowDown className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4 text-slate-300" />}
                </div>
              </TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((sub, index) => {
              const latest = getLatestRenewal(sub);
              const count = (sub.renewals || []).length || (sub.renewal_date ? 1 : 0);
              const currentLikeliness = getCurrentLikeliness(sub);
              const isSelected = selectedIds.includes(sub.subscription_id);
              return (
                <TableRow 
                  key={sub.subscription_id} 
                  className={`cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                  onClick={() => onEdit(sub)}
                  data-testid={`subscription-row-${sub.subscription_id}`}
                >
                  <TableCell 
                    onClick={(e) => {
                      e.stopPropagation();
                      const newChecked = !isSelected;
                      onSelectSubscription(sub.subscription_id, newChecked, index, e);
                    }}
                    className="cursor-pointer"
                  >
                    <Checkbox
                      checked={isSelected}
                      aria-label={`Select ${sub.business_name}`}
                      data-testid={`select-subscription-${sub.subscription_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-slate-900">{sub.business_name}</p>
                  </TableCell>
                  <TableCell>
                    <Badge className={LIKELINESS_COLORS[currentLikeliness]}>
                      <LikelinessIcon type={currentLikeliness} />
                      <span className="ml-1">{currentLikeliness}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(sub.signup_date)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {formatCurrency(sub.initial_price)}
                      {sub.initial_paid && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{count} renewal{count !== 1 ? 's' : ''}</Badge>
                  </TableCell>
                  <TableCell>
                    {latest ? (
                      <div className="flex items-center gap-2">
                        <span className={latest.renewal_date && new Date(latest.renewal_date) < new Date() && !latest.paid ? "text-red-600" : "text-slate-600"}>
                          {formatDate(latest.renewal_date)}
                        </span>
                        <span className="text-slate-400">{formatCurrency(latest.renewal_price)}</span>
                        {latest.paid && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(sub)}>
                          <Edit className="w-4 h-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => onDelete(sub.subscription_id)}>
                          <Trash2 className="w-4 h-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            {subscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  No subscriptions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default SubscriptionTable;
